import axios, { AxiosResponse } from 'axios';

/**
 * Handles backend synchronization for reading progress using axios
 */
export class ProgressSync {
  private readonly baseURL: string;

  private static readonly ERROR_NO_AUTH_TOKEN = 'No auth token available';
  private static readonly HTTP_STATUS_NOT_FOUND = 404;

  constructor(
    private readonly isbn: string,
    private readonly getAuthToken: () => Promise<string | undefined>,
  ) {
    this.baseURL = `${import.meta.env.VITE_API_URL}/books/${this.isbn}/progress`;
  }

  /**
   * Load progress from backend
   */
  async loadProgress(): Promise<{
    currentBlock: number;
  } | null> {
    try {
      const token = await this.getAuthToken();
      if (!token) {
        throw new Error(ProgressSync.ERROR_NO_AUTH_TOKEN);
      }

      const response: AxiosResponse = await axios.get(this.baseURL, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data;
    } catch (error) {
      if (
        axios.isAxiosError(error) &&
        error.response?.status === ProgressSync.HTTP_STATUS_NOT_FOUND
      ) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Save progress to backend - only send the current block position
   * Backend handles all metadata (updatedAt, userAlmaId, etc.)
   */
  async saveProgress(currentBlock: number): Promise<boolean> {
    const token = await this.getAuthToken();
    if (!token) {
      throw new Error(ProgressSync.ERROR_NO_AUTH_TOKEN);
    }

    await axios.put(
      this.baseURL,
      {
        currentBlock, // Only send the essential data
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      },
    );

    return true;
  }
}
